import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { CheckCircleIcon } from '@phosphor-icons/react/CheckCircle';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type AudienceForm, IncludesGroupName } from '../../types.ts';
import { filterNonEmptyGroups } from '../../utils/filterNonEmptyGroups.ts';
import { ExcludeButton } from './components/ExcludeButton.tsx';
import { GroupList } from './components/groups/GroupList.tsx';
import type { DrawerState } from './drawerTypes.ts';

export type IncludeCriteriaProps = {
  setDrawerState: Dispatch<SetStateAction<DrawerState>>;
  onAddExclusion: Dispatch<SetStateAction<boolean>>;
  errorMessage?: string;
  isEdit: boolean;
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
  onMarketUnionCoerced?: () => void;
  isAddExclusionBlocked?: boolean;
  addExclusionBlockedReason?: string;
};

const IncludesGroupsName = `${IncludesGroupName}.groups` as const;

export const IncludeCriteria = ({
  setDrawerState,
  onAddExclusion,
  errorMessage,
  isEdit,
  isCreate,
  isCreateFromTemplate,
  onMarketUnionCoerced,
  isAddExclusionBlocked = false,
  addExclusionBlockedReason,
}: IncludeCriteriaProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { fields, append, update, remove } = useFieldArray<AudienceForm, typeof IncludesGroupsName>({
    name: IncludesGroupsName,
  });

  const observableNormalizedIncludeDefinition = useWatch<AudienceForm, typeof IncludesGroupsName>({
    name: IncludesGroupsName,
  });

  const isIncludesEmpty: boolean = useMemo(
    () => !filterNonEmptyGroups(observableNormalizedIncludeDefinition).length,
    [observableNormalizedIncludeDefinition]
  );

  return (
    <Box data-test="includes-panel" className={errorMessage ? 'criteria-error' : undefined}>
      <GroupList
        icon={<CheckCircleIcon size={20} color={theme.palette.success.main} />}
        title={t('Includes')}
        isEdit={isEdit}
        isCreate={isCreate}
        isCreateFromTemplate={isCreateFromTemplate}
        fieldName="normalized_definition.includes"
        fields={fields}
        update={update}
        remove={remove}
        append={append}
        setDrawerState={setDrawerState}
        errorMessage={errorMessage}
        onMarketUnionCoerced={onMarketUnionCoerced}
      />

      {isEdit && (
        <ExcludeButton
          disabled={isIncludesEmpty || isAddExclusionBlocked}
          disabledReason={isAddExclusionBlocked ? addExclusionBlockedReason : undefined}
          onAddExclusion={onAddExclusion}
          text={t('Add Exclusion')}
        />
      )}
    </Box>
  );
};
